/**
 * Utility functions for platform filtering in Social Posts History tab
 */

/**
 * List of valid social media platforms that can be used for filtering posts
 * These are the platforms that support social media post creation and viewing
 */
const SOCIAL_MEDIA_PLATFORMS = [
  'linkedin',
  'facebook',
  'instagram',
  'x',
  'twitter', // Alias for x
  'tiktok',
  'whatsapp',
  'youtube', // YouTube can have posts/videos
] as const;

/**
 * Check if a platform is a social media platform
 * @param platform - Platform identifier to check
 * @returns true if platform is a social media platform
 */
const isSocialMediaPlatform = (platform: string): boolean => {
  const normalizedPlatform = platform.toLowerCase();
  return SOCIAL_MEDIA_PLATFORMS.includes(normalizedPlatform as any);
};

/**
 * Extract connected social media platform names from user integrations object
 * Only returns platforms that are social media platforms (excludes Slack, Canva, etc.)
 * @param userIntegrations - User authentications object from Redux
 * @returns Array of social media platform names (e.g., ['linkedin', 'facebook', 'instagram'])
 */
export const getConnectedPlatforms = (userIntegrations: Record<string, any>): string[] => {
  if (!userIntegrations || typeof userIntegrations !== 'object') {
    return [];
  }

  // Filter out platforms that:
  // 1. Are social media platforms (not other integrations like Slack, Canva, etc.)
  // 2. Have valid authentication data
  // A platform is considered connected if it has an object with authorised: true or has access_token
  return Object.keys(userIntegrations).filter(platform => {
    // First check if it's a social media platform
    if (!isSocialMediaPlatform(platform)) {
      return false;
    }

    const integration = userIntegrations[platform];
    if (!integration || typeof integration !== 'object') {
      return false;
    }
    // Check if platform is authorized or has access token
    return integration.authorised === true || integration.access_token || integration.apiToken;
  });
};

/**
 * Get human-readable display name for a platform
 * @param platform - Platform identifier (e.g., 'x', 'linkedin')
 * @returns Display name (e.g., 'X/Twitter', 'LinkedIn')
 */
export const getPlatformDisplayName = (platform: string): string => {
  const platformNames: Record<string, string> = {
    linkedin: 'LinkedIn',
    x: 'X/Twitter',
    twitter: 'X/Twitter',
    facebook: 'Facebook',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    youtube: 'YouTube',
    whatsapp: 'WhatsApp',
    wordpress: 'WordPress',
    slack: 'Slack',
  };

  return platformNames[platform.toLowerCase()] || platform.charAt(0).toUpperCase() + platform.slice(1);
};

/**
 * Get platform logo from integration data
 * @param platform - Platform identifier
 * @param integrationData - Array of integration data from query cache
 * @returns Logo URL or empty string
 */
export const getPlatformLogo = (platform: string, integrationData?: any[]): string => {
  if (!integrationData || !Array.isArray(integrationData)) {
    return '';
  }

  const integration = integrationData.find(
    (item: any) => item?.meta?.toLowerCase() === platform.toLowerCase()
  );

  return integration?.logo || '';
};

/**
 * Client-side filtering helper for immediate UI updates (optional)
 * @param posts - Array of posts
 * @param selectedPlatforms - Array of selected platform names
 * @returns Filtered array of posts
 */
export const filterPostsByPlatforms = (posts: any[], selectedPlatforms: string[]): any[] => {
  if (!selectedPlatforms || selectedPlatforms.length === 0) {
    return posts;
  }

  return posts.filter(post => {
    if (!post.integrations || typeof post.integrations !== 'object') {
      return false;
    }

    // Check if post has any of the selected platforms in its integrations
    return selectedPlatforms.some(platform => {
      const platformIntegration = post.integrations?.[platform];
      return platformIntegration !== undefined && platformIntegration !== null;
    });
  });
};

